import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Modal } from 'flowbite-react';

import {
    type BioCandidate, BioCandidateStatusEnum, userAtom, Brief,
} from '@/components/with_auth/State';
import { FETCH_BIO_BATCH, UPDATE_BIO_CANDIDATE, UPDATE_USER } from '@/components/onboarding/Actions';
import { getNewOnboardingState, BIO_NAMES } from '@/components/onboarding/Shared';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals';
import Pencil from '@/components/commons/icons/Pencil';
import PanelContainer from '@/components/commons/panel_container';

import bioStyles from './Bio.module.css';
import StarterBioCard from './StarterBioCard';
import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import BioHelpLink from './BioHelpLink';
import customModalTheme from './UploadFileModalTheme';

const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

function SelectStarterBioPanel({
    isModal = false,
    isEdit,
    openBrief,
    extendContinue = null,
    overrideBack = null,
}: bioPageProps) {
    const router = useRouter();
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [noDataModal, setNoDataModal] = useState(false);
    const [user] = useRecoilState(userAtom);

    const [updateUser] = useMutation(UPDATE_USER);
    const [updateCandidate] = useMutation(UPDATE_BIO_CANDIDATE);
    const { loading, data: bioData, refetch: refetchBioBatch } = useQuery(FETCH_BIO_BATCH, {
        fetchPolicy: 'no-cache',
    });

    const [selectedBio, setSelectedBio] = useState<string>();

    const [brief, setBrief] = useState<Brief>(openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex)
        : user.briefs[0]);

    useEffect(() => {
        const currentBrief = openBrief
            ? user.briefs.find((item) => item.id === openBrief.briefIndex)
            : user.briefs[0];

        if (currentBrief) {
            setBrief(currentBrief);
        }
    }, [user, openBrief]);

    const handleBackNavigation = useCallback(() => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_SELECT_IMPORT_METHOD);
        }
        // If we add router to the dependency array below it makes tests go in an infinite loop
        // because when the useEffect below checks that there are no valid candidates, it calls
        // this function to push a new route, which updates router, which updates this callback,
        // which is a dep of the useEffect below which makes it run again and call this again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModal, overrideBack]);

    // check if we have any bioBatch candidates, if not
    // go back to the career history page
    useEffect(() => {
        if (loading) {
            return;
        }
        const { bioBatches } = bioData.user;
        const currentBioBatch = bioBatches.find(
            (item) => item.id === brief?.bioBatch?.id,
        );

        if (!currentBioBatch || !currentBioBatch?.candidates.length) {
            handleBackNavigation();
        } else {
            const candidates = Array.from<BioCandidate>(currentBioBatch.candidates);

            const statusSuccess = candidates.filter((candidate) => (candidate.status === 'succeeded' || candidate.status === 'selected') && candidate);
            statusSuccess.forEach((candidate) => {
                // find the selected status...
                if (candidate.status === 'selected') {
                    setSelectedBio(candidate.id);
                }
            });
            // no successful statuses
            if (statusSuccess.length === 0) {
                setNoDataModal(true);
                return;
            }
            candidates.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
            // check for single candidate to adjust label
            // use the single entry in statusSucceess to retrieve
            // single candidate data to avoid searching candidates again

            if (statusSuccess.length === 1) {
                setOptions([{ ...statusSuccess[0], label: 'Bio Candidate' }]);
                return;
            }

            const newOptions = candidates.map((item, i) => item.status && ({
                ...item,
                label: `Option ${optionLabels[i]}`,
            }));
            setOptions(newOptions);
        }
    }, [loading, bioData, handleBackNavigation, brief?.bioBatch?.id]);

    const selectOption = async (id) => {
        // TODO(tom) go through all other
        // candidates and update backend with rejected (not picked)
        // or hallucination (Help, this is non sense)

        // // deselect current candidate
        if (selectedBio) {
            await updateCandidate({
                variables: {
                    input: {
                        id: selectedBio,
                        attributes: {
                            status: BioCandidateStatusEnum.Succeeded,
                        },
                    },
                },
            });
        }
        await updateCandidate({
            variables: {
                input: {
                    id,
                    attributes: {
                        status: BioCandidateStatusEnum.Selected,
                    },
                },
            },
        });

        setSelectedBio(id);
    };

    const rejectOption = async (id) => {
        // set it to hallucination, aka it was so bad
        // it felt like you were hallucinating.
        await updateCandidate({
            variables: {
                input: {
                    id,
                    attributes: {
                        status: BioCandidateStatusEnum.Hallucination,
                    },
                },
            },
        });
        await refetchBioBatch();
    };

    // Handle continue, goes to the edit bio because
    // they didn't select a candidate
    const handleContinue = async () => {
        if (!selectedBio) {
            return;
        }
        setIsLoading(true);

        if (isModal && extendContinue) {
            extendContinue('', isEdit);
        } else {
            await updateUser({
                variables: {
                    updateUserInput: {
                        attributes: {
                            onboardingState: getNewOnboardingState(user, 0),
                        },
                    },
                },
            });
            router.push(routes.BIO_REVISE_BIO);
        }
    };

    const handleSkip = async () => {
        setIsLoading(true);

        if (isModal && extendContinue) {
            // passing skip tells the modal navigation to
            // go forward 2
            extendContinue('skip', isEdit);
        } else {
            await updateUser({
                variables: {
                    updateUserInput: {
                        attributes: {
                            // Go 2 pages forward, if they didn't select a bio
                            // then they need to enter it manual, we can't tweak
                            onboardingState: getNewOnboardingState(user, 2),
                        },
                    },
                },
            });
            router.push(routes.BIO_EDIT_BIO);
        }
    };

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={2} totalSteps={5} isModal={isModal}>
            <div className="w-full py-2 px-2 sm:px-5">
                <BioPageHeader
                    header="Select the option that fits you best"
                    subHeader="We&apos;ve generated a few bio samples to get you started. Which one sounds the most like you? Don&apos;t worry, you&apos;ll be able to edit the text yourself next. "
                    subHeaderWidth="max-w-[560px]"
                />
                <div className="flex row justify-center flex-wrap gap-10">
                    {
                        options.map((item) => (

                            (item.status === 'succeeded' || item.status === 'selected')

                            && (
                                <StarterBioCard
                                    header={item.label}
                                    body={item.bio}
                                    onClick={() => selectOption(item.id)}
                                    onReject={() => rejectOption(item.id)}
                                    key={item.id}
                                    isSelected={selectedBio === item.id}
                                />
                            )
                        ))
                    }
                </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center w-full">
                <BioHelpLink
                    icon={<Pencil className="h-4 w-4 mr-2 text-cleanSlate" />}
                    text="Don't help me, I'll write my own"
                    externalStyles="shrink-0 pt-8"
                    onClick={handleSkip}
                />
                <NavButtons
                    containerStyles={bioStyles.buttonGroupWithoutBio}
                    handleBackNavigation={handleBackNavigation}
                    handleContinue={handleContinue}
                    isLoading={isLoading}
                />
            </div>
            {noDataModal && (
                <Modal className="absolute z-[1000]" theme={customModalTheme} show={noDataModal} position="center" size="sm">
                    <p className="text-[20px] font-bold">No data found</p>
                    <p>
                        We couldn&apos;t extract any useful
                        information from the documents you provided.
                    </p>

                    <p>Skip to manual entry</p>
                    <Button
                        color="dark"
                        className="bg-cleanSlate"
                        onClick={handleSkip}
                    >
                        Manual Entry
                    </Button>
                </Modal>
            )}
        </PanelContainer>
    );
}

export default SelectStarterBioPanel;
